import axios from 'axios'
import { ApiResponse, Sorteio, Participante, Resultado, SystemTimeResponse } from '../types'
import { handleApiError } from '../utils/errorHandler'

// Função para detectar a URL base da API automaticamente
function getApiBaseUrl(): string {
  // Se houver variável de ambiente, usar ela
  const envUrl = (import.meta as any).env?.VITE_API_URL
  if (envUrl) {
    console.log('🔧 API URL (variável de ambiente):', envUrl)
    return envUrl
  }
  
  // Detectar automaticamente o hostname/IP atual
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  const port = '8888'
  
  // Se estiver acessando via IP (não localhost), usar o mesmo IP para o backend
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const apiUrl = `${protocol}//${hostname}:${port}/api`
    console.log('🔧 API URL (detectada por IP):', apiUrl, '- Hostname:', hostname)
    return apiUrl
  }
  
  // Caso contrário, usar localhost
  const apiUrl = `http://localhost:${port}/api`
  console.log('🔧 API URL (localhost):', apiUrl)
  return apiUrl
}

const apiBaseUrl = getApiBaseUrl()

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000, // Aumentado para 30 segundos para requisições pela rede
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor para tratamento de erros e refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Tentar refresh token apenas se for 401 e ainda não tentou
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await api.post('/auth/refresh', { refreshToken })
          const { token } = response.data
          
          localStorage.setItem('authToken', token)
          originalRequest.headers.Authorization = `Bearer ${token}`
          
          return api(originalRequest)
        }
      } catch (refreshError: any) {
        // Refresh falhou, limpar dados e redirecionar para login
        localStorage.removeItem('authToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        // Marcar refresh error como tratado para não mostrar toast duplicado
        if (refreshError) {
          refreshError._errorHandled = true
        }
        // Redirecionar após um pequeno delay para mostrar possível toast
        setTimeout(() => {
          window.location.href = '/login'
        }, 500)
        return Promise.reject(refreshError)
      }
    }

    // Tratar erro apenas se não foi tentativa de refresh ou se refresh falhou
    // Não tratar erro 401 aqui se já tentou refresh (será tratado abaixo)
    if (!(error.response?.status === 401 && originalRequest._retry)) {
      handleApiError(error)
    } else {
      // Se é 401 após tentar refresh, ainda marca como tratado para evitar duplicação
      error._errorHandled = true
    }

    return Promise.reject(error)
  }
)

// Serviços para Sorteios
export const sorteiosService = {
  // Buscar todos os sorteios
  async getSorteios(): Promise<ApiResponse<Sorteio[]>> {
    const response = await api.get('/sorteios')
    return response.data
  },

  // Buscar sorteio por ID
  async getSorteioById(id: string): Promise<ApiResponse<Sorteio>> {
    const response = await api.get(`/sorteios/${id}`)
    return response.data
  },

  // Criar novo sorteio (admin)
  async createSorteio(sorteio: Omit<Sorteio, 'id'>): Promise<ApiResponse<Sorteio>> {
    const response = await api.post('/sorteios', sorteio)
    return response.data
  },

  // Atualizar sorteio (admin)
  async updateSorteio(id: string, sorteio: Partial<Sorteio>): Promise<ApiResponse<Sorteio>> {
    const response = await api.put(`/sorteios/${id}`, sorteio)
    return response.data
  },

  // Deletar sorteio (admin)
  async deleteSorteio(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/sorteios/${id}`)
    return response.data
  }
}

// Serviços para Participantes
export const participantesService = {
  // Participar de um sorteio
  async participarSorteio(sorteioId: string, participante: Omit<Participante, 'id' | 'dataInscricao' | 'sorteioId'>): Promise<ApiResponse<Participante>> {
    const response = await api.post(`/sorteios/${sorteioId}/participar`, participante)
    return response.data
  },

  // Buscar participantes de um sorteio
  async getParticipantes(sorteioId: string): Promise<ApiResponse<Participante[]>> {
    const response = await api.get(`/sorteios/${sorteioId}/participantes`)
    return response.data
  },

  // Verificar se usuário já participa de um sorteio
  async verificarParticipacao(sorteioId: string, cpf: string): Promise<ApiResponse<boolean>> {
    const response = await api.get(`/sorteios/${sorteioId}/participacao/${cpf}`)
    return response.data
  }
}

// Serviços para Resultados
export const resultadosService = {
  // Buscar todos os resultados
  async getResultados(): Promise<ApiResponse<Resultado[]>> {
    const response = await api.get('/resultados')
    return response.data
  },

  // Buscar resultado por ID do sorteio
  async getResultadoBySorteio(sorteioId: string): Promise<ApiResponse<Resultado>> {
    const response = await api.get(`/resultados/sorteio/${sorteioId}`)
    return response.data
  },

  // Realizar sorteio (admin)
  async realizarSorteio(sorteioId: string): Promise<ApiResponse<Resultado>> {
    const response = await api.post(`/sorteios/${sorteioId}/sortear`)
    return response.data
  }
}

export const systemService = {
  async getServerTime(): Promise<SystemTimeResponse> {
    const response = await api.get<SystemTimeResponse>('/system/time', {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0'
      }
    })
    return response.data
  }
}

export default api
